import { Button, Card, Group, List, Text } from '@mantine/core';
import { useRouter } from 'next/navigation';
import { useContext, useMemo } from "react";
import { DateTime } from "luxon";
import { Trip } from '../../lib/interfaces';
import { TSCachedImg } from "../TSImageCache";
import { useGoogleImages, revokeCachedGooglePlaceImages } from '../../lib/google';
import { showAddEditTrip } from "../AddEditTrip/AddEditTrip";
import { SheetsContext } from "../Sheets";
import { useIsWide } from '../../lib/hooks';
import { realDate } from '../../lib/time';

export function TripsList({ userTrips }: { userTrips: Trip[] }) {
    const router = useRouter();
    const sheets = useContext(SheetsContext);
    // startdate is nullable, and on legacy trips it can be the 1900 "no date" sentinel. Sort undated
    // trips (either flavour) to the end rather than letting them throw or masquerade as 1900 trips.
    const trips = [ ...userTrips ].sort((a, b) => {
        const aDate = realDate(a.startdate);
        const bDate = realDate(b.startdate);
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate.compareTo(aDate);
    });

    const items = trips.map((item, index) =>
        <Card
            aria-label='Trip Card'
            key={index}
            p={0}
            m={0}
            onClick={() => router.push(`/trip/${item.docid}`)}
        >
            <TripImage trip={item} />
            <Group gap={0} wrap='nowrap' py={5} justify="space-between" grow>
                <Text truncate="end">
                    {item.title}
                </Text>
                <Text style={{ textAlign: 'right' }} truncate='end'>
                    {realDate(item.startdate)?.toLocalDateTime().toLocaleString(DateTime.DATE_MED) ?? ''}
                </Text>
            </Group>
        </Card>);

    if (items.length === 0) {
        return <div
            className='trip-list'
            style={{
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
            }}
        >
            <div style={{ paddingBottom: '1rem' }}>You don&apos;t have any trips yet!</div>
            <Button onClick={() => showAddEditTrip(sheets, {})}>Plan your first trip</Button>
        </div>;
    } else {
        // `flex: 1` bounds the list to the tab panel (overriding the global `* { flex-shrink: 0 }`)
        // so it scrolls here rather than overflowing under the footer. Same as SavedPlacesList.
        return <div style={{ flex: 1, overflowY: 'auto' }}>
            <List px='xs' className='trip-list'>{items}</List>
        </div>;
    }
}

function useAppropriateImageUrl(trip: Trip) {
    // If there is no custom image url but there is a place URL, look up the place and
    // use the first image for the place.
    const { googlePics } = useGoogleImages(trip.img ? null : trip.placeid);

    return useMemo(
        () => {
            if (!trip.img && trip.placeid) {
                if (googlePics && googlePics.length > 0) {
                    return googlePics[0].url400h ?? googlePics[0].url;
                } else {
                    return undefined;
                }
            } else {
                return trip.img400h ?? trip.img;
            }
        },
        [ trip.img, trip.placeid, trip.img400h, googlePics ]
    );
}

export function TripImage(props: { trip: Trip }) {
    const url = useAppropriateImageUrl(props.trip);
    const isWide = useIsWide();
    const imageHeight = (isWide ?? false) ? 'clamp(200px, 26.5vw, 400px)' : '200px';

    return <TSCachedImg
        style={{ height: imageHeight }}
        src={url}
        alt={props.trip.title ?? 'Unknown Trip'}
        lazyLoading
        onFetchFailure={() => {
            // TODO trigger a re-render of this object, which will re-fetch from cache now that we've revoked
            props.trip.placeid && revokeCachedGooglePlaceImages(props.trip.placeid);
        }}
    />;
}
